import { type CompanyAlarm } from '../redux/companies/companies-types'
import { type DeviceAlarmTypeKey, FLOW_LIMIT } from '../redux/alarm-settings/alarm-types'
import { ApiUtil } from './api-utils'
import {
  type DateRange,
  type DownloadFileResponse,
  type FailedResponse,
  type SuccessResponseWithoutPayload
} from './types'
import { mapAlarmSettingsResponse, mapCheckUserResponse } from './response-mappers'
import { type AlarmSettingsResponse, type CheckUserResponsePayload } from './company-api-types'
import { type AlarmSettings } from '../redux/settings/settings-type'
import { CsvType } from '../constants/csv-type'

/* API related constants */
const COMPANY_API_PATH = '/company'

/* Request types */

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type SetCompanyAlarmRequest = {
  alarmType: DeviceAlarmTypeKey | null
  state: boolean
  maxFlowPerHour?: number | null
}

/* Response types */

interface CheckUserSuccessfulResponse {
  succeeded: true
  payload: CheckUserResponsePayload
}

export type CheckUserResponse = CheckUserSuccessfulResponse | FailedResponse

export type SetCompanyAlarmResponse = SuccessResponseWithoutPayload | FailedResponse

interface CompanyAlarmSettingsSuccessfulResponse {
  succeeded: true
  payload: AlarmSettingsResponse
}

export type CompanyAlarmSettingsResponse = CompanyAlarmSettingsSuccessfulResponse | FailedResponse

/* Export(s) */

/**
 * Backend API for company-related operations.
 */
export const CompanyApi = {
  checkUser,
  downloadMeterDataCsvStartingFromDate,
  downloadMeterDataCsvWithinSpecificDates,
  getAlarmSettings,
  setCompanyAlarm,
  updateAlarmSettings
}

export type CompanyApiType = typeof CompanyApi

/* API methods */

async function checkUser(token: string): Promise<CheckUserResponse> {
  const apiDesc = 'check user request'
  const url = ApiUtil.getApiUrl(`${COMPANY_API_PATH}/checkUser`)

  try {
    const response = await ApiUtil.doPostRequest(url, {}, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapCheckUserResponse(payload)
    return {
      succeeded: true,
      payload: parsedPayload
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

async function downloadMeterDataCsvStartingFromDate(
  token: string,
  input: { startDate: number, downloadType: CsvType }
): Promise<DownloadFileResponse> {
  const apiDesc = 'download meter data csv starting from date request'
  const url = ApiUtil.getApiUrl(`${COMPANY_API_PATH}/meterDataCsv`)

  const requestBody = {
    type: input.downloadType,
    startDate: ApiUtil.formatEpochSecondsToDateString(input.startDate)
  }

  return await ApiUtil.postDownloadFileRequest({ url, token, apiDesc, requestBody })
}

async function downloadMeterDataCsvWithinSpecificDates(
  token: string,
  payload: DateRange
): Promise<DownloadFileResponse> {
  const apiDesc = 'download meter data withing specific dates request'
  const url =
        ApiUtil.getApiUrl(`${COMPANY_API_PATH}/meterDataCsv`)

  const requestBody = {
    type: CsvType.DATE_SPECIFIC,
    startDate: ApiUtil.formatEpochSecondsToDateString(payload.startDate),
    endDate: ApiUtil.formatEpochSecondsToDateString(payload.endDate)
  }

  return await ApiUtil.postDownloadFileRequest({ url, token, apiDesc, requestBody })
}

/**
 * Get company alarm settings by company ID.
 * @param token Access token.
 * @param companyId Target company ID.
 */
async function getAlarmSettings(token: string, companyId: string): Promise<CompanyAlarmSettingsResponse> {
  const apiDesc = 'get company alarm settings request'
  const url = ApiUtil.getApiUrl(`${COMPANY_API_PATH}/${companyId}/alarm_settings`)

  try {
    const response = await ApiUtil.doGetRequest(url, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapAlarmSettingsResponse(payload)
    return {
      succeeded: true,
      payload: parsedPayload
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Update company alarm settings.
 * @param token Access token.
 * @param companyId Target company ID.
 * @param payload Alarm settings to be updated.
 */
async function updateAlarmSettings(
  token: string,
  companyId: string,
  payload: AlarmSettings
): Promise<CompanyAlarmSettingsResponse> {
  const apiDesc = 'update company alarm settings request'
  const url = ApiUtil.getApiUrl(`${COMPANY_API_PATH}/${companyId}/alarm_settings`)

  const requestBody = {
    emailEnabled: payload.emailEnabled,
    emailInterval: payload.emailInterval,
    emailRecipients: payload.emailRecipients,
    smsEnabled: payload.smsEnabled,
    smsInterval: payload.smsInterval,
    smsRecipients: payload.smsRecipients,
    temperatureAlertsEnabled: payload.temperatureAlertsEnabled,
    temperatureAlertLowerLimit: payload.temperatureAlertLowerLimit,
    temperatureAlertUpperLimit: payload.temperatureAlertUpperLimit
  }

  try {
    const response = await ApiUtil.doPostRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    const payload: unknown = await response.json()
    const parsedPayload = mapAlarmSettingsResponse(payload)
    return {
      succeeded: true,
      payload: parsedPayload
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}

/**
 * Set specific company alarm state.
 * @param token Access token.
 * @param payload Input for the request.
 */
async function setCompanyAlarm(token: string, payload: CompanyAlarm): Promise<SetCompanyAlarmResponse> {
  const apiDesc = 'set company alarm request'
  const url = ApiUtil.getApiUrl(`${COMPANY_API_PATH}/${payload.companyId}/set_alarm`)

  const requestBody: SetCompanyAlarmRequest = {
    alarmType: payload.alarmType?.companyAlarmType ?? null,
    state: payload.state,
    maxFlowPerHour: payload.alarmType === FLOW_LIMIT ? payload.maxFlowPerHour : null
  }

  try {
    const response = await ApiUtil.doPostRequest(url, requestBody, token)
    if (!response.ok) {
      return await ApiUtil.errorResponse(response, apiDesc)
    }

    return {
      succeeded: true
    }
  } catch (e) {
    return ApiUtil.unknownExceptionResponse(e, url, apiDesc)
  }
}
